// 선택 언어 상태, localStorage 동기화, t() 번역 헬퍼를 제공하는 Context
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SUPPORTED_LOCALES, translations } from '../i18n/translations';

const STORAGE_KEY = 'tarot-app-locale';
// SimsimPlay 포털(embed.js)이 상단 바 언어 메뉴 선택을 저장하는 키.
// 포털이 언어의 단일 소스이므로, 있으면 이 값을 우선한다.
const PORTAL_KEY = 'ssp_lang';

const LanguageContext = createContext(null);

function getByPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
}

// 포털이 저장한 언어를 읽되, 게임이 지원하는 언어일 때만 반환한다.
// (포털은 zh도 지원하지만 게임 번역에는 없으므로 그 경우 null → en 폴백)
function readPortalLocale() {
  try {
    const s = localStorage.getItem(PORTAL_KEY);
    if (s && SUPPORTED_LOCALES.includes(s)) return s;
  } catch {
    /* ignore */
  }
  return null;
}

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const portal = readPortalLocale();
    if (portal) return portal;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
    } catch {
      /* ignore */
    }
    return 'en';
  });

  const setLocale = useCallback((next) => {
    if (!SUPPORTED_LOCALES.includes(next)) return;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (path, vars) => {
      const table = translations[locale] || translations.en;
      let str = getByPath(table, path);
      if (str == null) str = getByPath(translations.en, path) ?? path;
      if (vars && typeof str === 'string') {
        return Object.keys(vars).reduce((s, k) => s.replaceAll(`{${k}}`, String(vars[k])), str);
      }
      return str;
    },
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  // 인터벌 콜백이 stale closure 없이 현재 locale 을 읽도록 ref 로 추적.
  const localeRef = useRef(locale);
  localeRef.current = locale;

  // 포털 상단 바의 언어 메뉴 선택을 게임 언어에 반영한다.
  // - 같은 탭: index.html 의 setItem 래퍼가 쏘는 'ssp-lang-change' 이벤트
  // - 다른 탭: 표준 'storage' 이벤트
  // - 폴백: embed.js 는 자체 이벤트를 쏘지 않으므로, 위 경로가 막혀도(캐시/CSP 등)
  //   ssp_lang 을 주기적으로 폴링해 확실히 동기화한다.
  useEffect(() => {
    const syncFromPortal = () => {
      const portal = readPortalLocale();
      if (portal && portal !== localeRef.current) setLocale(portal);
    };
    const onStorage = (e) => {
      if (e.key === PORTAL_KEY) syncFromPortal();
    };
    window.addEventListener('ssp-lang-change', syncFromPortal);
    window.addEventListener('storage', onStorage);
    const poll = setInterval(syncFromPortal, 600);
    return () => {
      window.removeEventListener('ssp-lang-change', syncFromPortal);
      window.removeEventListener('storage', onStorage);
      clearInterval(poll);
    };
  }, [setLocale]);

  useEffect(() => {
    const titles = {
      en: 'en',
      ko: 'ko',
      ja: 'ja',
      zh: 'zh',
      fr: 'fr',
      de: 'de',
      es: 'es',
    };
    document.documentElement.lang = titles[locale] || 'en';
    document.title = t('meta.appTitle');
  }, [locale, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
